import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenLOCOReport from "./HiddenLOCOReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A80D2C",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
};

// ─── PER-SCALE ACCENT COLOURS ─────────────────────────────────────────────────
const SCALE_COLOR = {
  I:  "#A80D2C",
  EO: "#1A7A4A",
  EC: "#D79A1E",
};

// ─── SCALE DEFINITIONS ────────────────────────────────────────────────────────
const SCALE_DEFS = [
  {
    code:    "I",
    key:     "internal",
    label:   "Internal",
    icon:    "🎯",
    tagline: "Personal agency, effort, competence, and ownership.",
    overview:
      "People who score high on Internal Locus of Control believe their actions, effort, and decisions are the primary drivers of what happens to them. They take ownership of outcomes, persist in the face of obstacles, and rarely attribute failures to external forces.",
    orgNote:
      "A high Internal orientation is strongly associated with initiative, accountability, and resilience in professional settings. In leadership, it supports execution discipline and a bias toward action. The risk is underestimating the legitimate role of context, relationships, and systemic factors in shaping outcomes.",
    devSteps: [
      "Continue owning outcomes — your accountability mindset is a core professional asset.",
      "Practise mapping stakeholder influences before committing to a plan.",
      "Build feedback loops so effort is tied to visible, measurable results.",
      "Guard against frustration when results do not match effort in genuinely uncertain situations.",
    ],
  },
  {
    code:    "EO",
    key:     "external_others",
    label:   "External-Others",
    icon:    "🤝",
    tagline: "Influence of seniors, power holders, and key others.",
    overview:
      "People who score high on External-Others believe that outcomes are significantly shaped by the decisions, preferences, and actions of powerful others — bosses, senior leaders, influential peers, or gatekeepers. They are attuned to hierarchy and relational dynamics.",
    orgNote:
      "A moderate EO orientation supports political awareness and stakeholder sensitivity — valuable in complex organisations. A high EO score can indicate over-reliance on approval or authority, reducing initiative and self-direction. Development should help the person distinguish between genuine dependency and strategic alignment.",
    devSteps: [
      "Identify decisions you are delaying because you are waiting for approval that may not be necessary.",
      "Build a track record of self-initiated action in lower-stakes situations.",
      "Use stakeholder awareness as a strategic asset, not a reason to defer all choices.",
      "Explore whether senior influence is a real constraint or a limiting belief.",
    ],
  },
  {
    code:    "EC",
    key:     "external_chance",
    label:   "External-Chance",
    icon:    "🎲",
    tagline: "Luck, timing, chance, and uncertainty.",
    overview:
      "People who score high on External-Chance believe that luck, fate, timing, and unpredictable circumstances play a significant role in determining outcomes. They may be less confident that effort consistently produces results.",
    orgNote:
      "A moderate EC orientation reflects realistic awareness that the world is uncertain — a healthy attribute in volatile environments. High EC scores may indicate a fatalistic attribution style that reduces planning, effort, and risk-taking. The key development challenge is separating genuine unpredictability from learned helplessness.",
    devSteps: [
      "Reframe 'luck' events: identify which factors in a past outcome were actually controllable.",
      "Build a habit of proactive planning even in uncertain conditions.",
      "Use scenario-based thinking to prepare for multiple outcomes, reducing dependence on favourable chance.",
      "Pair awareness of uncertainty with a clear personal action plan that is independent of outcomes.",
    ],
  },
];

// ─── BAND LOGIC ───────────────────────────────────────────────────────────────
const BAND_RANGES = {
  I:  { High: "30–40", Moderate: "21–29", Low: "10–20" },
  EO: { High: "30–40", Moderate: "21–29", Low: "10–20" },
  EC: { High: "30–40", Moderate: "21–29", Low: "10–20" },
};

const BAND_DESCRIPTIONS = {
  I: {
    High:     "You strongly exhibit an Internal Locus of Control — you believe your own actions, decisions, and effort are the primary determinants of outcomes. You are highly accountable and self-driven.",
    Moderate: "You moderately exhibit an Internal orientation. You take ownership of outcomes in most situations while acknowledging that external factors sometimes play a meaningful role.",
    Low:      "You only weakly exhibit an Internal orientation. You tend to attribute outcomes more to external factors than to your own actions, which can limit initiative and persistence.",
  },
  EO: {
    High:     "You strongly exhibit an External-Others orientation — you believe that powerful people and authority figures largely determine what happens to you. You are highly attuned to hierarchy and approval signals.",
    Moderate: "You moderately exhibit an External-Others orientation. You are politically aware and sensitive to the influence of key stakeholders while retaining reasonable confidence in your own agency.",
    Low:      "You only weakly exhibit an External-Others orientation. You rarely attribute outcomes to the decisions of others and are generally comfortable acting without senior endorsement.",
  },
  EC: {
    High:     "You strongly exhibit an External-Chance orientation — you believe that luck, fate, and unpredictable circumstances are major drivers of outcomes. This can reduce proactive planning and effort investment.",
    Moderate: "You moderately exhibit an External-Chance orientation. You acknowledge the role of luck and uncertainty without letting it dominate your decision-making or effort levels.",
    Low:      "You only weakly exhibit an External-Chance orientation. You are confident that consistent effort and planning — rather than luck — determine outcomes in most situations.",
  },
};

function getBandLabel(score) {
  if (score == null) return "—";
  if (score >= 30) return "High";
  if (score >= 21) return "Moderate";
  return "Low";
}

function getBandColor(label) {
  if (label === "High")     return "#17643b";
  if (label === "Moderate") return "#8a5a00";
  return "#9b1c1c";
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────────
function Skeleton({ h = 24, w = "100%", r = 8 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: "linear-gradient(90deg,#EAF0F6 25%,#f5f8fc 50%,#EAF0F6 75%)",
      backgroundSize: "200% 100%",
      animation: "loco-shimmer 1.4s infinite",
    }} />
  );
}

// ─── TRIANGLE CHART ───────────────────────────────────────────────────────────
function TriangleChart({ scores, dominantCode, loading }) {
  const cx = 150, cy = 155, maxR = 98;
  const keys   = ["I", "EO", "EC"];
  const angles = [-90, 150, 30];
  const MAX    = 40;

  function polar(deg, r) {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  const vertices  = angles.map(a => polar(a, maxR));
  const rings     = [10, 20, 30, 40];
  const scorePoints = keys.map((k, i) => {
    const r = ((scores?.[k] ?? 0) / MAX) * maxR;
    return polar(angles[i], Math.max(r, 2));
  });
  const polyStr = scorePoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const labelConfig = [
    { key: "I",  anchor: "middle", dx: 0,  dy: -14 },
    { key: "EO", anchor: "end",    dx: -6, dy:  14 },
    { key: "EC", anchor: "start",  dx:  6, dy:  14 },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <svg viewBox="0 0 300 310" width="100%" style={{ maxWidth: 290 }}>
        {rings.map(v => {
          const pts = angles.map(a => polar(a, (v / MAX) * maxR));
          return <polygon key={v} points={pts.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={T.line} strokeWidth={1} />;
        })}
        {rings.map(v => {
          const p = polar(-90, (v / MAX) * maxR);
          return <text key={`rv-${v}`} x={p.x + 4} y={p.y} fontSize="9" fill="#94a3b8" dominantBaseline="middle">{v}</text>;
        })}
        {vertices.map((v, i) => (
          <line key={i} x1={cx} y1={cy} x2={v.x} y2={v.y} stroke={T.line} strokeWidth={1.5} />
        ))}
        {!loading && (
          <>
            <polygon points={polyStr} fill={`${T.navy}20`} stroke={T.navy} strokeWidth={2.5} strokeLinejoin="round"
              style={{ transition: "all .8s cubic-bezier(.4,0,.2,1)" }} />
            {scorePoints.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={5}
                fill={dominantCode === keys[i] ? SCALE_COLOR[keys[i]] : "#fff"}
                stroke={SCALE_COLOR[keys[i]]} strokeWidth={2} />
            ))}
          </>
        )}
        {labelConfig.map(({ key, anchor, dx, dy }, i) => {
          const v     = vertices[i];
          const color = SCALE_COLOR[key];
          const isDom = dominantCode === key;
          const def   = SCALE_DEFS.find(d => d.code === key);
          return (
            <g key={key}>
              <text x={v.x + dx} y={v.y + dy - 5} textAnchor={anchor} dominantBaseline="middle"
                fontSize="11" fontWeight="700" fill={isDom ? color : T.ink}>{key}</text>
              <text x={v.x + dx} y={v.y + dy + 9} textAnchor={anchor} dominantBaseline="middle"
                fontSize="8.5" fill={T.slate}>{def?.label}</text>
            </g>
          );
        })}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="9" fill={T.slate} fontWeight={600} letterSpacing="1">LOCUS</text>
        <text x={cx} y={cy + 8} textAnchor="middle" fontSize="9" fill={T.slate} fontWeight={600} letterSpacing="1">PROFILE</text>
      </svg>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
        {keys.map(k => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--loco-text-xs)" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: SCALE_COLOR[k] }} />
            <span style={{ color: T.ink }}>{k}: <strong>{loading ? "—" : (scores?.[k] ?? "—")}</strong></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SCALE CARD (expandable) ──────────────────────────────────────────────────
function ScaleCard({ def, score, bandLabel, isDominant }) {
  const [open, setOpen] = useState(isDominant);
  const color    = SCALE_COLOR[def.code];
  const bColor   = getBandColor(bandLabel);

  return (
    <div style={{
      border: isDominant ? `2px solid ${color}` : `1px solid ${T.line}`,
      borderRadius: 16, overflow: "hidden",
      boxShadow: isDominant ? `0 4px 18px ${color}30` : "none",
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)",
        background: open
          ? isDominant ? `linear-gradient(90deg,${color}12,${color}06)` : `linear-gradient(90deg,${T.navy}10,${T.blue}06)`
          : "#fbfdff",
        border: "none", cursor: "pointer", gap: 12, textAlign: "left", transition: "background .2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: isDominant ? color : `${color}22`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            fontSize: "1.4rem",
          }}>
            {isDominant ? <span style={{ fontSize: "1.1rem" }}>{def.icon}</span> : <span>{def.icon}</span>}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: T.ink, fontSize: "var(--loco-text-sm)" }}>
              {def.label}
              {isDominant && (
                <span style={{ marginLeft: 8, fontSize: "var(--loco-text-xs)", background: color, color: "#fff", borderRadius: 999, padding: "2px 8px", verticalAlign: "middle" }}>
                  Dominant
                </span>
              )}
            </div>
            <div style={{ fontSize: "var(--loco-text-xs)", color: T.slate, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {def.tagline}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {score != null && (
            <span style={{ fontWeight: 700, color: isDominant ? color : T.navy, fontSize: "clamp(.9rem,2vw,1.1rem)" }}>{score}</span>
          )}
          <span style={{ color: isDominant ? color : T.navy, fontSize: "1.2rem", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>▾</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "16px 18px 18px", background: "#fff", borderTop: `1px solid ${T.line}` }}>
          {/* Dimension badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <span style={{ background: `${color}14`, border: `1px solid ${color}40`, borderRadius: 999, padding: "3px 10px", fontSize: "var(--loco-text-xs)", fontWeight: 600, color }}>
              {def.code} — {def.label}
            </span>
            {bandLabel && bandLabel !== "—" && (
              <span style={{ background: `${bColor}15`, border: `1px solid ${bColor}40`, borderRadius: 999, padding: "3px 10px", fontSize: "var(--loco-text-xs)", fontWeight: 600, color: bColor }}>
                {bandLabel} · {BAND_RANGES[def.code]?.[bandLabel]}
              </span>
            )}
          </div>

          <p style={{ margin: "0 0 10px", fontSize: "var(--loco-text-sm)", lineHeight: 1.65 }}>{def.overview}</p>
          <p style={{ margin: "0 0 12px", color: T.slate, fontSize: "var(--loco-text-xs)", lineHeight: 1.65, fontStyle: "italic" }}>{def.orgNote}</p>

          {/* Active band block */}
          {bandLabel && bandLabel !== "—" && (() => {
            const desc = BAND_DESCRIPTIONS[def.code]?.[bandLabel] ?? "";
            return desc ? (
              <div style={{
                border: `1px solid ${color}40`, background: `${color}08`,
                borderRadius: 10, padding: "10px 14px", marginBottom: 12,
              }}>
                <div style={{ fontWeight: 700, fontSize: "var(--loco-text-xs)", color, marginBottom: 4 }}>
                  {bandLabel} ({BAND_RANGES[def.code]?.[bandLabel]})
                  <span style={{ marginLeft: 8, fontSize: "var(--loco-text-xs)", background: color, color: "#fff", borderRadius: 999, padding: "1px 7px" }}>
                    Your band
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: "var(--loco-text-xs)", lineHeight: 1.6, color: T.ink }}>{desc}</p>
              </div>
            ) : null;
          })()}

          <div>
            <strong style={{ color: T.navy, fontSize: "var(--loco-text-xs)" }}>Development steps:</strong>
            <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
              {def.devSteps.map((step, i) => (
                <li key={i} style={{ marginBottom: 4, fontSize: "var(--loco-text-xs)", lineHeight: 1.55 }}>{step}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function LocoResults() {
  const location  = useLocation();
  const navState  = location.state || {};

  const [userInfo,      setUserInfo]      = useState(null);
  const [locoData,      setLocoData]      = useState(null);
  const [scores,        setScores]        = useState(null);
  const [profile,       setProfile]       = useState(null);
  const [breakdown,     setBreakdown]     = useState([]);
  const [submissionTime, setSubmissionTime] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [pdfData,       setPdfData]       = useState(null);
  const [isGenerating,  setIsGenerating]  = useState(false);
  const generatingRef = useRef(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        if (navState.data && navState.respondent) { hydrate(navState); return; }
        if (navState.scores?.data)                { hydrate(navState.scores); return; }

        const email = navState.email ?? null;
        const phone = navState.phone ?? null;
        if (!email && !phone) {
          setError("No result data found. Please complete the survey first.");
          return;
        }

        const API_BASE = import.meta.env.VITE_API_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/loco`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error ?? `Server error ${res.status}`);
        }
        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response.");
        hydrate(json);

      } catch (e) {
        setError(e.message || "Failed to load report data. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    function hydrate(payload) {
      const respondent = payload.respondent ?? {};
      const d = payload.data ?? {};

      setUserInfo({
        name:       respondent.name        ?? navState.name       ?? null,
        email:      respondent.email       ?? navState.email      ?? null,
        phone:      respondent.phone       ?? navState.phone      ?? null,
        company:    respondent.company     ?? navState.company    ?? null,
        department: respondent.department  ?? navState.department ?? null,
        submittedAt: respondent.submitted_at ?? null,
      });

      // ── Capture submission_time for PDF ──
      const st = respondent.submitted_at ?? respondent.submission_time ?? d.submission_time ?? null;
      setSubmissionTime(st);

      setLocoData(d);
      setProfile(d.profile ?? null);
      setBreakdown(d.breakdown ?? []);

      const flat = {};
      Object.values(d.scores ?? {}).forEach(s => { flat[s.code] = s.score; });
      setScores(flat);
    }

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ─────────────────────────────────────────────────────────
  const dominantCode = (() => {
    if (!scores) return null;
    const [top] = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    return top?.[0] ?? null;
  })();

  const dominantDef = SCALE_DEFS.find(d => d.code === dominantCode);

  const scalesForDisplay = SCALE_DEFS.map(def => ({
    def,
    score:      scores?.[def.code] ?? null,
    bandLabel:  getBandLabel(scores?.[def.code]),
    isDominant: dominantCode === def.code,
  }));

  const dominantLabel = dominantDef ? `${dominantDef.code} — ${dominantDef.label}` : "—";

  // ── Date string ────────────────────────────────────────────────────────────
  const dateStr = submissionTime
    ? formatDate(new Date(submissionTime))
    : loading ? "—" : formatDate(new Date());

  // ── PDF handler ────────────────────────────────────────────────────────────
  const handleDownloadPDF = useCallback(() => {
    if (loading || !scores || generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo:        userInfo        || { name: "Participant" },
      locoData:        locoData        || {},
      scores:          scores          || {},
      dominantCode:    dominantCode    || null,
      insightText:     profile?.report_text || "",
      submission_time: submissionTime,   // ← passes assessment date to PDF
    });
  }, [loading, scores, userInfo, locoData, dominantCode, profile, submissionTime]);

  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  // ── Scale breakdown counts ─────────────────────────────────────────────────
  const scaleCounts = breakdown.reduce((acc, row) => {
    acc[row.scale] = (acc[row.scale] ?? 0) + 1;
    return acc;
  }, {});

  if (loading && !scores) return (
    <div style={{ padding: "2rem", textAlign: "center", fontFamily: "sans-serif" }}>Loading Report…</div>
  );
  if (error && !scores) return (
    <div style={{ padding: "2rem", color: "red", fontFamily: "sans-serif" }}>Error: {error}</div>
  );

  return (
    <>
      <div className="loco-report" id="loco-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

        <style>{`
          :root {
            --loco-text-xs:   clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
            --loco-text-sm:   clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
            --loco-text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
            --loco-text-lg:   clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
            --loco-text-xl:   clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
            --loco-space-4:  1rem;    --loco-space-6:  1.5rem;
            --loco-space-8:  2rem;    --loco-space-10: 2.5rem;
            --loco-space-12: 3rem;    --loco-space-16: 4rem;
            --loco-radius: 20px;      --loco-radius-sm: 12px;
            --loco-shadow: 0 18px 40px rgba(14,39,68,.10);
            --loco-font: 'Satoshi', sans-serif;

            /* ── Logo vars (for absolute-positioned logo panel in cover) ──*/
           --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);

            /* ── Cover coordination vars ──────────────────────────
               --loco-wedge-h  : height of the decorative white wedge
               --loco-cvr-pad  : top/bottom padding of .cover-inner
               brand min-height = wedge-h - cvr-pad  →  brand bottom
               always flush with wedge bottom at every pixel          */
            --loco-wedge-h: clamp(80px, 22vw, 250px);
            --loco-cvr-pad: var(--loco-space-16);
          }

          /* ── Root ── */
          .loco-report {
            font-family: var(--loco-font);
            background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
            color: #1D2733;
            line-height: 1.55;
          }
          .loco-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--loco-space-8) var(--loco-space-4) var(--loco-space-16);
          }

          /* ── Page shells ── */
          .loco-report .page {
            background: #ffffff;
            border: 1px solid #DCE5EF;
            border-radius: 28px;
            box-shadow: var(--loco-shadow);
            overflow: hidden;
            margin-bottom: var(--loco-space-8);
          }

          /* ── Cover ── */
          .loco-report .cover {
            min-height: 92vh;
            position: relative;
            background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                        linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
            color: white;
          }
          .loco-report .cover-inner {
            padding: var(--loco-space-16);
            display: flex; flex-direction: column;
            justify-content: space-between;
            min-height: 92vh;
          }
          /* Decorative white wedge in top-right corner — purely visual */
          .loco-report .cover-corner-decor {
            position: absolute; top: 0; right: 0;
            width: clamp(160px, 40vw, 500px);
            height: var(--loco-wedge-h);           /* driven by CSS var */
            z-index: 1; pointer-events: none;
          }
          /* Brand row — min-height = wedge-h − cover-inner padding
             so the brand row bottom is always flush with the wedge bottom */
          .loco-report .brand {
            display: flex;
            align-items: center;
            position: relative;
            z-index: 3;
            /* reserve space so badge doesn't hide under logo panel */
            padding-right: calc(var(--logo-w) * 0.35);
          }
          /* Logo panel — simple flex child inside .brand, no absolute positioning */
          .loco-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
            .loco-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .loco-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .loco-report .badge {
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem,1.5vw,0.85rem);
            white-space: nowrap; flex-shrink: 0;
          }
          .loco-report .cover-title { margin-top: var(--loco-space-16); }
          .loco-report .cover-title h2 {
            margin: 0 0 var(--loco-space-4);
            font-size: clamp(2.2rem, 5vw, 4.4rem);
            line-height: .96; color: #fff;
          }
          .loco-report .cover-title p {
            font-size: var(--loco-text-lg);
            max-width: 100%; color: rgba(255,255,255,.84); text-align: justify;
          }
          .loco-report .cover-grid {
            display: grid; grid-template-columns: repeat(3, 1fr);
            gap: var(--loco-space-4); margin-top: var(--loco-space-12);
          }
          .loco-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: 18px; padding: 1rem 1.1rem;
          }
          .loco-report .meta span   { display: block; font-size: var(--loco-text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
          .loco-report .meta strong { font-size: var(--loco-text-base); color: white; }
          .loco-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--loco-space-8); }
          .loco-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem;
          }
          .loco-report .footer-note {
            display: flex; justify-content: space-between; gap: var(--loco-space-4);
            color: rgba(255,255,255,.72); font-size: var(--loco-text-xs);
            margin-top: var(--loco-space-10);
          }

          /* ── Inner pages ── */
          .loco-report .page-inner { padding: var(--loco-space-10); }
          .loco-report .topbar {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: var(--loco-space-4); margin-bottom: var(--loco-space-8);
          }
          .loco-report .section-kicker {
            color: #c62828; font-size: var(--loco-text-xs);
            text-transform: uppercase; letter-spacing: .12em; font-weight: 700;
          }
          .loco-report .section-title { margin: .35rem 0 0; font-size: var(--loco-text-xl); color: #0f2744; }
          .loco-report .pill {
            display: inline-flex; align-items: center; gap: .45rem;
            padding: .5rem .85rem; border-radius: 999px;
            font-size: var(--loco-text-xs); font-weight: 700;
            text-transform: uppercase; letter-spacing: .06em; white-space: nowrap;
          }
          .loco-report .pill-red   { background: #fef2f2; color: #c62828; }
          .loco-report .pill-slate { background: #f1f5f9; color: #5B677A; }
          .loco-report .pill-green { background: #f0fdf4; color: #16a34a; }

          /* ── Cards ── */
          .loco-report .card {
            background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
            border: 1px solid #DCE5EF; border-radius: var(--loco-radius);
            padding: var(--loco-space-6);
          }
          .loco-report .card h3 { margin: 0 0 .75rem; font-size: var(--loco-text-lg); color: #0f2744; }
          .loco-report .lead { color: #334155; font-size: var(--loco-text-base); margin: 0; text-align: justify; }

          /* ── Sub-panel ── */
          .loco-report .sub-panel {
            background: #fbfdff; border: 1px solid #DCE5EF;
            border-radius: 16px; padding: 1.25rem;
          }
          .loco-report .sub-panel h4 { margin: 0 0 .65rem; font-size: var(--loco-text-base); color: #0f2744; }
          .loco-report .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

          /* ── Score tiles ── */
          .loco-report .score-tile-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .loco-report .score-tile {
            border-radius: 14px; border: 1px solid #DCE5EF;
            padding: .7rem .85rem;
            background: linear-gradient(180deg,#fff,#f8fbff); min-width: 0; text-align: center;
          }
          .loco-report .score-tile-label { font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: #5B677A; font-weight: 700; margin-bottom: 2px; }
          .loco-report .score-tile-value { font-size: 1.7rem; font-weight: 800; line-height: 1.1; }
          .loco-report .score-tile-band  { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-top: 2px; }

          /* ── Bar rows ── */
          .loco-report .bar-row { display: grid; grid-template-columns: 175px 1fr 60px; gap: 1rem; align-items: center; margin-bottom: .8rem; }
          .loco-report .bar-label { font-size: var(--loco-text-sm); font-weight: 700; color: #0f2744; }
          .loco-report .bar-track { height: 22px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
          .loco-report .bar-fill  { height: 100%; border-radius: 999px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .loco-report .bar-val   { text-align: right; font-size: var(--loco-text-sm); font-weight: 700; font-variant-numeric: tabular-nums; }

          /* ── Callout / info-box ── */
          .loco-report .callout {
            background: linear-gradient(180deg,#fff9e8,#fffdf2);
            border: 1px solid #F2D58A; border-radius: 16px;
            padding: 14px 16px; font-size: var(--loco-text-sm);
            line-height: 1.6; color: #1D2733; text-align: justify;
          }
          .loco-report .info-box {
            background: #eff6ff; border: 1px solid #bfdbfe;
            border-left: 4px solid #A80D2C;
            border-radius: 12px; padding: 1rem 1.3rem;
            font-size: var(--loco-text-sm); line-height: 1.7; color: #1e3a5f;
          }

          /* ── Back cover ── */
          .loco-report .back-cover { color: white; }
          .loco-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; min-height: 56vh; }
          .loco-report .back-cover-header {
            display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
            gap: .5rem; padding: clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem);
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .loco-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem,4vw,3rem); color: #fff; }
          .loco-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(.85rem,2vw,1.2rem); text-align: justify; }
          .loco-report .contact-grid {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px,100%), 1fr));
            gap: clamp(.4rem,1.2vw,1rem); margin-top: var(--loco-space-8);
          }
          .loco-report .contact {
            background: rgba(168,16,38,.1); border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px,2vw,16px); padding: clamp(.6rem,1.5vw,.9rem); min-width: 0;
          }
          .loco-report .contact span   { display: block; color: rgba(255,255,255,.64); font-size: var(--loco-text-xs); margin-bottom: .3rem; }
          .loco-report .contact strong { font-size: clamp(.78rem,1.6vw,.92rem); word-break: break-word; }
          .loco-report .light { color: rgba(255,255,255,.68); }

          /* ── Download button ── */
          #loco-pdf-btn {
            position: fixed; bottom: 2rem; right: 2rem; z-index: 9999;
            display: inline-flex; align-items: center; gap: .6rem;
            padding: .75rem 1.4rem;
            background: linear-gradient(135deg, #F3AC26, #D79A1E);
            color: white; border: 1px solid #F3AC26; border-radius: 999px;
            box-shadow: 0 8px 24px rgba(198,40,40,.35), 0 0 14px rgba(243,173,38,.45);
            cursor: pointer; font-family: var(--loco-font); font-weight: 700; font-size: 1rem;
            transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          }
          #loco-pdf-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(198,40,40,.45); }
          #loco-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #loco-pdf-btn:disabled { opacity: .72; cursor: not-allowed; }
          #loco-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,.35); border-top-color: white;
            border-radius: 50%; animation: loco-spin .7s linear infinite; flex-shrink: 0;
          }

          /* ── Animations ── */
          @keyframes loco-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
          @keyframes loco-fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          @keyframes loco-spin    { to{transform:rotate(360deg)} }
          .loco-fade-up { animation: loco-fadeUp .55s ease both; }

          /* ── Tablet (≤ 768px) ── */
          @media (max-width: 768px) {
            .loco-report .report { padding: 1.25rem .75rem 3rem; }
            .loco-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
            .loco-report .cover { min-height: 88vh; }
            .loco-report .cover-inner { padding: 2.25rem; min-height: 88vh; --loco-cvr-pad: 2.25rem; }
            .loco-report .cover-title { margin-top: 2.5rem; }
            .loco-report .cover-grid { gap: .75rem; margin-top: 1.5rem; }
            .loco-report .page-inner { padding: 1.75rem; }
            .loco-report .topbar { flex-wrap: wrap; gap: .5rem; }
            .loco-report .pill { font-size: 10px; padding: .35rem .6rem; }
            .loco-report .two-col { grid-template-columns: 1fr; }
            .loco-report .score-tile-grid { grid-template-columns: repeat(3, 1fr); }
            .loco-report .bar-row { grid-template-columns: 120px 1fr 44px; gap: .6rem; }
            .loco-report .bar-track { height: 18px; }
            .loco-report .contact-grid { grid-template-columns: repeat(auto-fit, minmax(min(140px,100%),1fr)); }
          }

          /* ── Mobile (≤ 480px) ── */
          @media (max-width: 480px) {
            .loco-report .report { padding: .75rem .5rem 2.5rem; }
            .loco-report .page { border-radius: 16px; margin-bottom: .9rem; }
            .loco-report .cover { min-height: 100svh; }
            .loco-report .cover-inner { padding: 1.25rem; min-height: 100svh; --loco-cvr-pad: 1.25rem; }
            .loco-report .cover-title { margin-top: 1.5rem; }
            .loco-report .cover-title h2 { font-size: clamp(1.5rem, 7vw, 2rem); line-height: 1.08; }
            .loco-report .cover-title p { font-size: .875rem; }
            .loco-report .cover-grid { grid-template-columns: repeat(auto-fit, minmax(min(130px,100%),1fr)); gap: .5rem; margin-top: 1.1rem; }
            .loco-report .meta { padding: .6rem .75rem; }
            .loco-report .cover-badges { margin-top: .9rem; gap: .4rem; }
            .loco-report .cover-badge { font-size: .7rem; padding: .3rem .55rem; }
            .loco-report .footer-note { flex-direction: column; gap: .2rem; font-size: .68rem; margin-top: 1rem; }
            .loco-report .page-inner { padding: 1rem; }
            .loco-report .topbar { flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
            .loco-report .section-title { font-size: 1.25rem; }
            .loco-report .pill { font-size: 9px; padding: .25rem .5rem; }
            .loco-report .card { padding: .875rem; border-radius: 14px; }
            .loco-report .card h3 { font-size: .95rem; margin-bottom: .45rem; }
            .loco-report .lead { font-size: .84rem; }
            .loco-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .loco-report .bar-label { font-size: .77rem; font-weight: 600; }
            .loco-report .bar-track { height: 14px; }
            .loco-report .bar-val { font-size: .77rem; text-align: right; }
            .loco-report .score-tile-grid { grid-template-columns: repeat(3, 1fr); }
            .loco-report .contact-grid { grid-template-columns: 1fr; gap: .5rem; margin-top: 1rem; }
            .loco-report .closing h2 { font-size: clamp(1.45rem,5.5vw,2rem); }
            .loco-report .closing p { font-size: .88rem; }
          }

          /* ── Tiny (≤ 390px) ── */
          @media (max-width: 390px) {
            .loco-report .report { padding: .5rem .35rem 2rem; }
            .loco-report .page { border-radius: 12px; margin-bottom: .75rem; }
            .loco-report .cover { min-height: 100svh; }
            .loco-report .cover-inner { padding: 1rem; min-height: 100svh; --loco-cvr-pad: 1rem; }
            .loco-report .badge { font-size: .55rem; padding: .25rem .5rem; }
            .loco-report .cover-title { margin-top: 1rem; }
            .loco-report .cover-title h2 { font-size: clamp(1.35rem,6.5vw,1.7rem); line-height: 1.1; }
            .loco-report .cover-title p { font-size: .78rem; }
            .loco-report .cover-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .9rem; }
            .loco-report .meta { padding: .5rem .65rem; border-radius: 12px; }
            .loco-report .meta strong { font-size: .82rem; }
            .loco-report .cover-badges { margin-top: .75rem; gap: .3rem; }
            .loco-report .cover-badge { font-size: .65rem; padding: .25rem .45rem; }
            .loco-report .footer-note { flex-direction: column; gap: .15rem; font-size: .62rem; margin-top: .75rem; }
            .loco-report .page-inner { padding: .85rem; }
            .loco-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .loco-report .contact-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .75rem; }
          }

          /* ── Print ── */
          @media print {
            .loco-report { background: white; }
            .loco-report .report { max-width: none; padding: 0; }
            .loco-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; }
            #loco-pdf-btn { display: none !important; }
          }
        `}</style>

        {/* ── Floating PDF Button ── */}
        <button id="loco-pdf-btn" onClick={handleDownloadPDF} disabled={isGenerating || loading || !scores} aria-label="Download PDF Report">
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">
              {/* Decorative corner wedge — purely visual, never overlaps text */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                <div className="cover-title">
                    
                  <h2>Locus of Control <br></br>Inventory</h2>
                  <p>
                    Based on Levenson's (1974) multidimensional framework. Measures your attribution
                    style across three independent dimensions — Internal, External-Others, and
                    External-Chance — which together reveal your tendency to attribute outcomes to
                    personal agency, powerful others, or chance.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : (userInfo?.name || "—")}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                  <div className="meta"><span>Dominant Scale</span><strong>{loading ? "—" : dominantLabel}</strong></div>
                </div>

                {userInfo?.company && (
                  <div style={{ marginTop: "1rem", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 14, padding: ".9rem 1rem", display: "inline-flex", gap: "1.5rem", flexWrap: "wrap" }}>
                    <div><span style={{ fontSize: "var(--loco-text-xs)", color: "rgba(255,255,255,.65)" }}>Organisation</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.company}</strong></div>
                    {userInfo.department && <div><span style={{ fontSize: "var(--loco-text-xs)", color: "rgba(255,255,255,.65)" }}>Department</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.department}</strong></div>}
                  </div>
                )}

                <div className="cover-badges">
                  {["Score Summary", "Triangle Profile", "Primary Profile", "Scale Analysis", "Development Guide"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
                <div>Based on Levenson's (1974) multidimensional locus of control framework.</div>
                <div>Scored across three scales: I · EO · EC</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — SCORE SUMMARY & TRIANGLE
          ══════════════════════════════════════════════════════ */}
          <section className="page loco-fade-up">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Score Summary</h2>
                </div>
                <div className="pill pill-red">Dominant: {loading ? "—" : dominantLabel}</div>
              </div>

              {/* How to read */}
              <div className="info-box">
                <strong>How to read this report:</strong> Three independent scores are reported — Internal (I), External-Others (EO), and External-Chance (EC). Each ranges from <strong>10 to 40</strong>. Higher scores indicate stronger endorsement of that attribution style. Low = 10–20 · Moderate = 21–29 · High = 30–40.
              </div>
              <br></br>

              {/* Score tiles + bars */}
              <div className="two-col" style={{ marginBottom: "1.5rem" }}>

                {/* Left — Triangle + score tiles */}
                <div className="sub-panel">
                  <h4>Attribution Profile</h4>
                  <TriangleChart scores={scores} dominantCode={dominantCode} loading={loading} />
                  <div className="score-tile-grid" style={{ marginTop: ".85rem" }}>
                    {scalesForDisplay.map(({ def, score, bandLabel, isDominant }) => {
                      const color  = SCALE_COLOR[def.code];
                      const bColor = getBandColor(bandLabel);
                      return (
                        <div key={def.code} className="score-tile"
                          style={isDominant ? { border: `2px solid ${color}`, background: `linear-gradient(180deg,${color}10,#fff)` } : {}}>
                          <div className="score-tile-label" style={{ color }}>{def.code}</div>
                          {loading
                            ? <div style={{ marginTop: 4 }}><Skeleton h={34} r={6} /></div>
                            : <div className="score-tile-value" style={{ color }}>{score ?? "—"}</div>
                          }
                          {!loading && <div className="score-tile-band" style={{ color: bColor }}>{bandLabel}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right — bars + dominant callout */}
                <div className="sub-panel">
                  <h4>Score Breakdown</h4>
                  <p style={{ color: T.slate, fontSize: "var(--loco-text-xs)", margin: "0 0 .75rem" }}>
                    Each scale scored from 10 (minimum) to 40 (maximum). Band thresholds: Low = 10–20 · Moderate = 21–29 · High = 30–40.
                  </p>
                  {scalesForDisplay.map(({ def, score, isDominant }) => {
                    const pct   = Math.min(100, ((score ?? 0) / 40) * 100);
                    const color = SCALE_COLOR[def.code];
                    return (
                      <div key={def.code} className="bar-row">
                        <span className="bar-label">{def.code} — {def.label}</span>
                        <div className="bar-track">
                          <div className="bar-fill" style={{
                            width: loading ? "0%" : `${pct}%`,
                            background: isDominant
                              ? `linear-gradient(90deg,${color},${color}88)`
                              : `linear-gradient(90deg,${T.navy}55,${T.slate}55)`,
                          }} />
                        </div>
                        <span className="bar-val" style={{ color: isDominant ? color : T.navy }}>{loading ? "—" : (score ?? "—")}</span>
                      </div>
                    );
                  })}

                  {/* Dominant band callout */}
                  {!loading && dominantDef && (
                    <div style={{ marginTop: ".75rem" }}>
                      <div className="callout">
                        <strong>Dominant scale: </strong>
                        {BAND_DESCRIPTIONS[dominantDef.code]?.[getBandLabel(scores?.[dominantDef.code])] ?? "—"}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Interpretation brackets */}
              <div>
                <div style={{ fontWeight: 700, fontSize: "var(--loco-text-base)", color: "#0f2744", marginBottom: ".75rem" }}>
                  Interpretation Brackets
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "1rem" }}>
                  {[
                    { label: "Low",      range: "10–20", bg: "#ffe9e9", color: "#9b1c1c", note: "Weak endorsement of that attribution style." },
                    { label: "Moderate", range: "21–29", bg: "#fff4d6", color: "#8a5a00", note: "Balanced or situational endorsement." },
                    { label: "High",     range: "30–40", bg: "#e8f6ea", color: "#17643b", note: "Strong endorsement of that attribution style." },
                  ].map(({ label, range, bg, color, note }) => (
                    <div key={label} className="card" style={{ textAlign: "center", padding: "1rem" }}>
                      <span style={{ display: "inline-block", padding: "4px 14px", borderRadius: 999, fontSize: "var(--loco-text-xs)", fontWeight: 700, background: bg, color, marginBottom: 8 }}>
                        {label} ({range})
                      </span>
                      <p style={{ margin: 0, fontSize: "var(--loco-text-xs)", color: T.slate, lineHeight: 1.5 }}>{note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — PRIMARY PROFILE
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Primary Profile</h2>
                </div>
                <div className="pill pill-slate">Profile Identification</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Your responses map to a specific attribution profile. The profile below describes your dominant pattern and what it means in professional contexts.
              </p>

              {/* Profile card */}
              {loading ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Skeleton h={22} w="40%" /><Skeleton h={16} w="60%" /><Skeleton h={80} />
                </div>
              ) : profile ? (
                <div className="card" style={{ borderLeft: `4px solid ${T.navy}`, padding: "1.4rem 1.6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: "1rem" }}>
                    <div>
                      <div style={{ fontSize: "var(--loco-text-xs)", textTransform: "uppercase", letterSpacing: ".1em", color: T.navy, fontWeight: 700, marginBottom: 4 }}>
                        Profile:
                      </div>
                      <h3 style={{ margin: "0 0 4px", fontSize: "1.15rem", color: T.navy }}>{profile.label}</h3>
                      <div style={{ fontSize: "var(--loco-text-xs)", color: T.slate }}>
                        Dominance Pattern: <strong style={{ color: T.ink }}>{profile.dominance_pattern}</strong>
                      </div>
                    </div>
                    {/* Grade pills */}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {["I", "EO", "EC"].map(code => {
                        const grade  = profile.grade?.[code] ?? getBandLabel(scores?.[code]);
                        const bColor = getBandColor(grade);
                        return (
                          <div key={code} style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "var(--loco-text-xs)", fontWeight: 700, color: T.slate, marginBottom: 3, textTransform: "uppercase", letterSpacing: ".06em" }}>{code}</div>
                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: "var(--loco-text-xs)", fontWeight: 700, background: `${bColor}18`, color: bColor, border: `1px solid ${bColor}40` }}>
                              {grade}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <hr style={{ border: "none", borderTop: `1px solid ${T.line}`, margin: ".6rem 0" }} />
                  <p style={{ margin: 0, fontSize: "var(--loco-text-sm)", lineHeight: 1.75, color: T.ink }}>
                    {profile.report_text || "—"}
                  </p>
                </div>
              ) : null}
              <br></br>

              {/* Summary note */}
              {!loading && (
                <div className="info-box">
                  <strong>Summary:</strong>{" "}
                  {dominantCode === "I"
                    ? "This profile suggests a strong ownership mindset with realistic awareness that not everything is fully controllable. For mid-senior roles, that is usually a useful combination when paired with influence skills and stakeholder management."
                    : dominantCode === "EO"
                    ? "This profile reflects high sensitivity to the influence of others. Paired with strong relationship skills, it can be an asset. Development should focus on expanding the comfort zone for autonomous, self-initiated action."
                    : "This profile reflects a pragmatic, balanced attribution style. The candidate likely uses contextual judgment across situations. Development should focus on building clearer personal decision rules to reduce ambiguity in high-stakes moments."}
                </div>
              )}

              <br></br>

              {/* Work style + leadership implication */}
              <div className="two-col">
                {[
                  {
                    title: "Work Style",
                    icon:  "💼",
                    body:  dominantCode === "I"
                      ? "Self-driven, accountable, and outcome-oriented. Likely to take initiative, follow through on commitments, and push for execution even when conditions are not ideal."
                      : dominantCode === "EO"
                      ? "Attentive to stakeholder signals and hierarchical expectations. Works well in structured environments with clear chains of authority."
                      : "Pragmatic and adaptive. Tends to pace effort against perceived predictability of the environment.",
                  },
                  {
                    title: "Leadership Implication",
                    icon:  "🚀",
                    body:  dominantCode === "I"
                      ? "Useful in roles requiring ownership, delivery discipline, and confidence in decision-making. Can model responsibility for team performance."
                      : dominantCode === "EO"
                      ? "Effective in relationship-driven leadership where buy-in from power holders is critical. May need support to act decisively without top-down endorsement."
                      : "Brings flexible judgment in uncertain environments. Leadership strength may lie in contextual reasoning rather than a fixed operating style.",
                  },
                ].map(({ title, icon, body }) => (
                  <div key={title} className="card">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ".6rem" }}>
                      <span style={{ fontSize: "1.2rem" }}>{icon}</span>
                      <h3 style={{ margin: 0, color: T.navy }}>{title}</h3>
                    </div>
                    <p className="lead">{loading ? <Skeleton h={60} /> : body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 4 — SCALE DEEP-DIVES (I · EO)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Scale Deep-Dives</h2>
                </div>
                <div className="pill pill-slate">I · EO</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                A detailed description of each scale's meaning and its implications in professional contexts. Your dominant scale is expanded by default.
              </p>

              <div style={{ display: "grid", gap: ".75rem" }}>
                {scalesForDisplay.slice(0, 3).map(({ def, score, bandLabel, isDominant }) => (
                  <ScaleCard key={def.code} def={def} score={score} bandLabel={bandLabel} isDominant={isDominant} />
                ))}
              </div>

              <br></br>

              <div className="two-col" style={{ marginBottom: "1.5rem" }}>
                <div className="card">
                  <h3>Understanding the Three Scales</h3>
                  <p className="lead">
                    Internal, External-Others, and External-Chance are independent dimensions — not a single spectrum. A person can score high on both I and EO, reflecting a belief that personal effort matters and that powerful relationships also shape outcomes. Profiles should be read holistically, not as a ranking.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    Locus of control is shaped by experience, culture, and context. With targeted development, people can shift their attribution style — strengthening internal agency where it is low, or moderating excessive internal attributions by building greater systemic and relational awareness.
                  </p>
                </div>
              </div>

              {/* Development guidance table */}
              <div>
                <div style={{ fontWeight: 700, fontSize: "var(--loco-text-base)", color: "#0f2744", marginBottom: ".75rem" }}>
                  Development Guidance
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Focus Area", "Recommendation"].map(h => (
                          <th key={h} style={{
                            background: "#eff6ff", color: T.navy,
                            border: `1px solid ${T.line}`, padding: "10px 14px",
                            textAlign: "left", fontSize: "var(--loco-text-xs)", fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: ".06em",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        [1, 2, 3, 4].map(i => (
                          <tr key={i}>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px" }}><Skeleton h={16} w={120} /></td>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px" }}><Skeleton h={16} /></td>
                          </tr>
                        ))
                      ) : (
                        [
                          {
                            area: "Development Priority",
                            rec: dominantCode === "I"
                              ? "Strengthen strategic influence and stakeholder mapping so effort converts to visible outcomes more consistently."
                              : dominantCode === "EO"
                              ? "Build independent decision-making capability. Practise committing to a course of action without waiting for senior validation."
                              : "Strengthen clear decision rules: when to act independently, when to align, and when to wait.",
                          },
                          {
                            area: "Coaching Focus",
                            rec: dominantCode === "I"
                              ? "Differentiate between controllable effort and uncontrollable factors. Build a habit of planning for uncertainty without reducing ownership."
                              : dominantCode === "EO"
                              ? "Identify the internal compass — what do you believe is right when no one is watching? Use that as a baseline for autonomous action."
                              : "Work on identifying default triggers for each attribution style. Increase self-awareness around when each lens is most useful.",
                          },
                          {
                            area: "Manager Support",
                            rec: dominantCode === "I"
                              ? "Give clear goals, timely feedback, and decision boundaries. Recognise outcomes publicly so the candidate sees the link between action and result."
                              : dominantCode === "EO"
                              ? "Create protected zones for autonomous decision-making. Avoid over-supervising — it reinforces the belief that outcomes depend on the manager."
                              : "Provide structured frameworks for decision-making in ambiguous situations. Clarity of expectations reduces paralysis.",
                          },
                          {
                            area: "Behavioural Watch",
                            rec: dominantCode === "I"
                              ? "Monitor for frustration or burnout when external factors block effort-driven results. Build resilience for situations where effort alone is insufficient."
                              : dominantCode === "EO"
                              ? "Watch for deference patterns in meetings — are they consistently deferring to authority even on topics within their own expertise?"
                              : "Observe whether uncertainty is used as a reason to delay action. Support the candidate in separating analysis from paralysis.",
                          },
                        ].map(({ area, rec }, i) => (
                          <tr key={area} style={{ background: i % 2 === 0 ? "#fff" : "#fafcff" }}>
                            <td style={{
                              border: `1px solid ${T.line}`, padding: "10px 14px",
                              fontSize: "var(--loco-text-xs)", fontWeight: 700, color: T.navy,
                              whiteSpace: "nowrap", verticalAlign: "top",
                            }}>{area}</td>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px", fontSize: "var(--loco-text-xs)", lineHeight: 1.65 }}>
                              {rec}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 5 — SCALE DEEP-DIVES (EC) + DEVELOPMENT GUIDE
          ══════════════════════════════════════════════════════ */}
          {/* <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Scale Deep-Dives &amp; Development</h2>
                </div>
                <div className="pill pill-slate">EC · Guidance</div>
              </div> */}

              {/* <div style={{ display: "grid", gap: ".75rem", marginBottom: "1.5rem" }}>
                {scalesForDisplay.slice(2).map(({ def, score, bandLabel, isDominant }) => (
                  <ScaleCard key={def.code} def={def} score={score} bandLabel={bandLabel} isDominant={isDominant} />
                ))}
              </div> */}

              {/* Understanding the three scales + coaching note */}
              {/* <div className="two-col" style={{ marginBottom: "1.5rem" }}>
                <div className="card">
                  <h3>Understanding the Three Scales</h3>
                  <p className="lead">
                    Internal, External-Others, and External-Chance are independent dimensions — not a single spectrum. A person can score high on both I and EO, reflecting a belief that personal effort matters and that powerful relationships also shape outcomes. Profiles should be read holistically, not as a ranking.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    Locus of control is shaped by experience, culture, and context. With targeted development, people can shift their attribution style — strengthening internal agency where it is low, or moderating excessive internal attributions by building greater systemic and relational awareness.
                  </p>
                </div>
              </div>

              {/* Development guidance table */}
              {/* <div>
                <div style={{ fontWeight: 700, fontSize: "var(--loco-text-base)", color: "#0f2744", marginBottom: ".75rem" }}>
                  Development Guidance
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Focus Area", "Recommendation"].map(h => (
                          <th key={h} style={{
                            background: "#eff6ff", color: T.navy,
                            border: `1px solid ${T.line}`, padding: "10px 14px",
                            textAlign: "left", fontSize: "var(--loco-text-xs)", fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: ".06em",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        [1, 2, 3, 4].map(i => (
                          <tr key={i}>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px" }}><Skeleton h={16} w={120} /></td>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px" }}><Skeleton h={16} /></td>
                          </tr>
                        ))
                      ) : (
                        [
                          {
                            area: "Development Priority",
                            rec: dominantCode === "I"
                              ? "Strengthen strategic influence and stakeholder mapping so effort converts to visible outcomes more consistently."
                              : dominantCode === "EO"
                              ? "Build independent decision-making capability. Practise committing to a course of action without waiting for senior validation."
                              : "Strengthen clear decision rules: when to act independently, when to align, and when to wait.",
                          },
                          {
                            area: "Coaching Focus",
                            rec: dominantCode === "I"
                              ? "Differentiate between controllable effort and uncontrollable factors. Build a habit of planning for uncertainty without reducing ownership."
                              : dominantCode === "EO"
                              ? "Identify the internal compass — what do you believe is right when no one is watching? Use that as a baseline for autonomous action."
                              : "Work on identifying default triggers for each attribution style. Increase self-awareness around when each lens is most useful.",
                          },
                          {
                            area: "Manager Support",
                            rec: dominantCode === "I"
                              ? "Give clear goals, timely feedback, and decision boundaries. Recognise outcomes publicly so the candidate sees the link between action and result."
                              : dominantCode === "EO"
                              ? "Create protected zones for autonomous decision-making. Avoid over-supervising — it reinforces the belief that outcomes depend on the manager."
                              : "Provide structured frameworks for decision-making in ambiguous situations. Clarity of expectations reduces paralysis.",
                          },
                          {
                            area: "Behavioural Watch",
                            rec: dominantCode === "I"
                              ? "Monitor for frustration or burnout when external factors block effort-driven results. Build resilience for situations where effort alone is insufficient."
                              : dominantCode === "EO"
                              ? "Watch for deference patterns in meetings — are they consistently deferring to authority even on topics within their own expertise?"
                              : "Observe whether uncertainty is used as a reason to delay action. Support the candidate in separating analysis from paralysis.",
                          },
                        ].map(({ area, rec }, i) => (
                          <tr key={area} style={{ background: i % 2 === 0 ? "#fff" : "#fafcff" }}>
                            <td style={{
                              border: `1px solid ${T.line}`, padding: "10px 14px",
                              fontSize: "var(--loco-text-xs)", fontWeight: 700, color: T.navy,
                              whiteSpace: "nowrap", verticalAlign: "top",
                            }}>{area}</td>
                            <td style={{ border: `1px solid ${T.line}`, padding: "10px 14px", fontSize: "var(--loco-text-xs)", lineHeight: 1.65 }}>
                              {rec}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div> */}

              {/* {error && (
                <div style={{ background: "#fff0f0", border: "1px solid #f5c6c6", borderRadius: 12, padding: "14px 18px", color: "#9b1c1c", fontSize: "var(--loco-text-sm)", marginTop: "1rem" }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </section> */} 

          {/* ══════════════════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight: "56vh",
            }}>
              <div className="back-cover-header">
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>Acumen Business Catalyst</div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing" style={{ padding: "0.1rem" }}>
                    <h2>Developing Your Locus of Control</h2>
                    <p>
                      Attribution style is a dynamic capability, not a fixed trait. Understanding whether you tend to attribute outcomes to your own effort, the influence of powerful others, or the role of chance — and the conditions under which each attribution serves or limits you — is the foundation of deliberate development. Acumen Business Catalyst uses assessments like this to turn behavioural data into focused conversations that build lasting professional effectiveness.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Application</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)", textAlign: "justify" }}>
                      For this participant, the most effective professional approach should draw on their dominant attribution style as a starting point, while deliberately building awareness of the other orientations. Each scale has its legitimate place — the goal is not to change who you are, but to develop the flexibility to recognise which lens you are using, and when a different one would serve you better.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership · Culture · Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Individual LOCI Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Locus of Control Inventory (LOCI). Based on Levenson's (1974) multidimensional locus of control framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator ── */}
      {pdfData && (
        <HiddenLOCOReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}